import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import Form from "react-bootstrap/Form";
import moment from "moment";
import ButtonsDays from "components/datestimes/ButtonsDays";
import DateTimesUtils from "core/utils/DateTimesUtils";

const FormTabProgTasks = ({
    item,
    setItem,
    days,
    setDays,
    optionsShown,
    setOptionsShown,
    option,
    setOption,
    inputRefs,
}) => {
    const {t: _Tf} = useTranslation("forms");
    const {t: _Td} = useTranslation("datestimes");

    const dates = useRef({start: null, end: null});

    useEffect(() => {
        if (item.date_start) {
            dates.current.start = moment(item.date_start, "YYYY-MM-DD");
        } else {
            dates.current.start = moment();
            setItem && setItem({...item, date_start: dates.current.start.format("YYYY-MM-DD")});
        }

        if (item.is_periodic) {
            const values = item.values || {};
            switch (item.repeat) {
                case "w":
                    setDays(values.days || []);
                    break;
                case "m":
                case "y":
                    calculateOptions(item.repeat, dates.current.start);
                    switch (values.type) {
                        case "d": setOption("0"); break;
                        case "n": setOption("1"); break;
                        case "p": setOption("2"); break;
                        default:
                    }
                    break;
                default:
            }
        }
    }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const {name, type} = e.target;
        const value = type === "checkbox" ? e.target.checked : e.target.value;
        if (!setItem) return;
        const item_ = {...item};
        item_[name] = value;
        switch (name) {
            case "date_start":
                dates.current.start = moment(value, "YYYY-MM-DD");
                if (item_.is_periodic && ["m", "y"].includes(item_.repeat)) {
                    calculateOptions(item_.repeat, dates.current.start);
                }
                break;
            case "is_periodic":
                item_.every = 1;
                item_.repeat = "w";
                setDays([]);
                setOptionsShown([]);
                break;
            case "repeat":
                calculateOptions(value, dates.current.start);
                break;
            default:
        }
        setItem(item_);
    };

    const calculateOptions = (repeat, startDate) => {
        setOptionsShown([]);
        setDays([]);
        setOption("0");
        const start = startDate || dates.current.start || moment();
        const options_ = [];
        const day = start.format("D");
        switch (repeat) {
            case "m": {
                options_.push({id: "0", day, value: 0, type: "d", title: `El día ${day}`});
                const dayPlace = DateTimesUtils.dayMonthPlace(start.format("YYYY-MM-DD"));
                const weekday = start.format("d");
                const dayName = _Td("days_name." + weekday);
                options_.push({id: "1", day: weekday, value: dayPlace, type: "n", title: `${_Td("the_place." + dayPlace)} ${dayName.toLowerCase()}`});
                if (dayPlace >= 4) {
                    options_.push({id: "2", day: weekday, value: "last", type: "p", title: `${_Td("the_place.last")} ${dayName.toLowerCase()}`});
                }
                break;
            }
            case "y": {
                const month = start.format("M");
                options_.push({id: "0", day, value: month, type: "d", title: `El ${day} de ${_Td("months_name." + month)}`});
                const dayPlace = DateTimesUtils.dayMonthPlace(start.format("YYYY-MM-DD"));
                const weekday = start.format("d");
                const dayName = _Td("days_name." + weekday);
                options_.push({id: "1", day: weekday, value: month, place: dayPlace, type: "n", title: `${_Td("the_place." + dayPlace)} ${dayName.toLowerCase()} de ${_Td("months_name." + month)}`});
                if (dayPlace >= 4) {
                    options_.push({id: "2", day: weekday, value: month, place: "last", type: "p", title: `${_Td("the_place.last")} ${dayName.toLowerCase()} de ${_Td("months_name." + month)}`});
                }
                break;
            }
            default:
        }
        setOptionsShown(options_);
    };

    return (
        <>
            {/* Nombre */}
            <div className="row g-3 align-items-center">
                <div className="col-3">
                    <label className="col-form-label">{_Tf("common.name")} *</label>
                </div>
                <div className="col-9">
                    <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={item.name || ""}
                        onChange={handleChange}
                        ref={(el) => (inputRefs.current.name = el)}
                    />
                </div>
            </div>

            {/* Descripción */}
            <div className="row g-3 align-items-center mt-3">
                <div className="col-3">
                    <label className="col-form-label">{_Tf("common.description")}</label>
                </div>
                <div className="col-9">
                    <textarea
                        className="form-control"
                        rows="3"
                        name="description"
                        value={item.description || ""}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Tipo */}
            <div className="row g-3 align-items-center mt-3">
                <div className="col-3">
                    <label className="col-form-label">{_Tf("common.type")}</label>
                </div>
                <div className="col-9">
                    <Form.Select
                        name="type"
                        value={item.type || "p"}
                        onChange={handleChange}
                        style={{maxWidth: "160px"}}>
                        <option value="p">Preventivo</option>
                        <option value="c">Correctivo</option>
                    </Form.Select>
                </div>
            </div>

            {/* Fechas */}
            <div className="row g-3 align-items-center mt-3">
                <div className="col-3">
                    <label className="col-form-label">{_Tf("programming.start_date")}</label>
                </div>
                <div className="col-3">
                    <input
                        type="date"
                        className="form-control"
                        name="date_start"
                        value={item.date_start || ""}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-3 text-end">
                    <label className="col-form-label">{_Tf("programming.end_date")}</label>
                </div>
                <div className="col-3">
                    <input
                        type="date"
                        className="form-control"
                        name="date_end"
                        value={item.date_end || ""}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Horas */}
            <div className="row g-3 align-items-center mt-3">
                <div className="col-3">
                    <label className="col-form-label">{_Tf("programming.hour-prog")}</label>
                </div>
                <div className="col-3">
                    <input
                        type="time"
                        className="form-control"
                        name="compile"
                        value={item.compile || ""}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-3 text-end">
                    <label className="col-form-label">{_Tf("programming.hour-prog-start")}</label>
                </div>
                <div className="col-3">
                    <input
                        type="time"
                        className="form-control"
                        name="run"
                        value={item.run || ""}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <hr className="mt-5 mb-4" />

            {/* Switch periódico */}
            <div className="row g-3 align-items-center mt-3">
                <div className="form-check form-switch d-flex justify-content-center">
                    <input
                        type="checkbox"
                        id="is-periodic"
                        className="form-check-input"
                        name="is_periodic"
                        checked={!!item.is_periodic}
                        onChange={handleChange}
                    />
                    <label htmlFor="is-periodic" className="form-check-label ms-3">
                        {_Tf("programming.periodic")}
                    </label>
                </div>
            </div>

            {/* Opciones periódicas */}
            {item.is_periodic && (
                <div className="col-12">
                    <div className="row g-3 align-items-center mt-3">
                        <div className="col-3">
                            <label className="col-form-label">Repetir cada</label>
                        </div>
                        <div className="col-9 mb-2">
                            <input
                                type="number"
                                className="form-control text-end d-inline-block me-2"
                                name="every"
                                min="1"
                                max="100"
                                value={item.every || 1}
                                onChange={handleChange}
                                style={{maxWidth: "100px"}}
                            />
                            <Form.Select
                                className="d-inline-block"
                                name="repeat"
                                value={item.repeat || "d"}
                                onChange={handleChange}
                                style={{maxWidth: "130px"}}>
                                <option value="d">Días</option>
                                <option value="w">Semanas</option>
                                <option value="m">Meses</option>
                                <option value="y">Años</option>
                            </Form.Select>
                        </div>
                    </div>

                    {item.repeat === "w" && (
                        <div className="row mt-5 text-center">
                            <div className="col-12">
                                <h5>{_Tf("alert-setups.days")}</h5>
                            </div>
                            <ButtonsDays days={days} setDays={setDays} display={true} />
                        </div>
                    )}

                    {optionsShown.length > 0 &&
                        optionsShown.map((_option, index) => (
                            <div className="row mt-3" key={index}>
                                <div className="col-3"></div>
                                <div className="col-9">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="options"
                                            id={_option.id}
                                            value={_option.id}
                                            checked={_option.id === option}
                                            onChange={(e) => setOption(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor={_option.id}>
                                            {_option.title}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </>
    );
};

export default FormTabProgTasks;
